import React, { useEffect, useState, useCallback } from 'react';
import { staffAPI, PAYMENT_STATUS, ATTENDANCE_STATUS, EQUIPMENT_CATEGORIES, CONDITION_OPTIONS, PRESET_SHIFTS, GENDER_OPTIONS } from '../../api/staff';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import UserAvatar from '../UserAvatar';
import { Plus, Download, ClipboardList, Users, DollarSign, Package, X, ChevronDown, ChevronUp, Check, QrCode, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import QRModal from './QRModal';
import MassEquipmentModal from './MassEquipmentModal';
import DistributionTab from './DistributionTab';

// ─── AssignStaffModal (3 steps) ───────────────────────────────────────────────
function AssignStaffModal({ eventId, existingIds: existingIdsProp, onAssigned, onClose }) {
  const [step, setStep] = useState(0);
  const [allStaff, setAllStaff] = useState([]);
  const [allEquip, setAllEquip] = useState([]);
  const [selected, setSelected] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [configs, setConfigs] = useState({});
  const [sharedEquip, setSharedEquip] = useState({});
  const [saving, setSaving] = useState(false);
  // #AssignStaffRefreshExisting — antes existingIds venía solo del prop,
  // que podía estar stale si un QR fue aprobado u otro admin asignó
  // entre que el parent cargó y se abrió el modal. Ahora refrescamos al
  // abrir consultando event_staff directamente.
  const [existingIds, setExistingIds] = useState(existingIdsProp || []);

  useEffect(() => {
    Promise.all([
      staffAPI.list({ status: 'activo' }),
      staffAPI.catalogList({ is_active: true }),
      staffAPI.eventStaff(eventId).catch(() => ({ data: { items: [] } })),
    ]).then(([s, e, evStaff]) => {
      setAllStaff(s.data.items || []);
      setAllEquip(e.data.items || []);
      const fresh = (evStaff.data?.items || evStaff.data || []).map((a) => a.staff_member_id);
      // Merge prop con fresh para no perder IDs si la API falla
      setExistingIds(Array.from(new Set([...(existingIdsProp || []), ...fresh])));
    });
  }, [eventId, existingIdsProp]);

  const filteredStaff = searchQ ? allStaff.filter(s => (s.full_name||'').toLowerCase().includes(searchQ.toLowerCase())) : allStaff;

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    if (!configs[id]) setConfigs(prev => ({...prev, [id]: { position: '', total_amount: '' }}));
  };

  const handleAssign = async () => {
    setSaving(true);
    try {
      const assignments = selected.map(sid => {
        const cfg = configs[sid] || {};
        // Only include equipment with a valid quantity > 0
        const equipment = Object.entries(sharedEquip)
          .filter(([, v]) => v.enabled && v.qty && parseInt(v.qty) > 0)
          .map(([equipId, v]) => ({
            equipment_id: equipId,
            // Backend field names; previously sent `quantity`/`condition`
            // which pydantic silently dropped, leaving every assignment at
            // the default quantity_assigned=1 (#64).
            quantity_assigned: parseInt(v.qty),
            condition_on_delivery: v.condition || 'bueno',
          }));
        return {
          staff_member_id: sid,
          position_in_event: cfg.position || '',
          total_amount: parseFloat(cfg.total_amount) || 0,
          shifts: cfg.shifts || [], // Array of shift objects
          shift: cfg.shifts?.[0] || cfg.shift || null, // Keep single shift for backward compatibility
          zone: cfg.zone || null,
          location_in_event: cfg.location_in_event || null,
          equipment,
        };
      });
      const res = await staffAPI.assignStaff(eventId, assignments);
      // #AssignStaffPartialSuccess — el backend ahora retorna assigned + skipped.
      // Si hubo skipped, mostramos warning con los nombres en vez de un error
      // genérico. El flow no se rompe: si al menos 1 se asignó, cerramos OK.
      const assigned = res?.data?.assigned ?? assignments.length;
      const skipped = res?.data?.skipped || [];
      if (assigned > 0) {
        toast.success(`${assigned} persona(s) asignadas`);
      }
      if (skipped.length > 0) {
        const names = skipped.map((s) => s.full_name || s.staff_member_id).join(', ');
        toast.warning(`Ya estaban asignados: ${names}`, { duration: 6000 });
      }
      if (assigned === 0 && skipped.length === 0) {
        toast.error('No se asignó a nadie');
      }
      onAssigned();
      onClose();
    } catch(err) {
      toast.error(err?.response?.data?.detail || 'Error al asignar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy flex-shrink-0">
          <h3 className="font-bold text-white">Asignar Personal al Evento</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        {/* Step bar */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
          {['Seleccionar','Configurar','Equipo'].map((s, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${step === i ? 'text-soem-primary' : i < step ? 'text-green-600' : 'text-slate-400'}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${step === i ? 'border-soem-primary text-soem-primary' : i < step ? 'bg-green-600 border-green-600 text-white' : 'border-slate-300'}`}>
                  {i < step ? '✓' : i + 1}
                </span>
                {s}
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 ${i < step ? 'bg-green-600' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Step 0: Select staff */}
          {step === 0 && (
            <div className="space-y-3">
              <input className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                placeholder="Buscar personal..." value={searchQ} onChange={e => setSearchQ(e.target.value)} />
              <p className="text-xs text-slate-400">{selected.length} seleccionados</p>
              <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto">
                {filteredStaff.map(s => {
                  const already = existingIds.includes(s.id);
                  const isSelected = selected.includes(s.id);
                  const avatarUser = { id: s.id, first_name: s.first_name, last_name: s.last_name, name: s.full_name };
                  return (
                    <div key={s.id} onClick={() => !already && toggleSelect(s.id)}
                      className={`flex items-center gap-3 px-2 py-3 rounded-sm cursor-pointer ${already ? 'opacity-40 cursor-default' : isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-soem-primary border-soem-primary' : 'border-slate-300'}`}>
                        {isSelected && <Check size={12} className="text-white" />}
                      </div>
                      <UserAvatar user={avatarUser} size="sm" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-soem-navy">{s.full_name}</p>
                        <p className="text-xs text-slate-400">{s.position}</p>
                      </div>
                      {already && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Ya asignado</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1: Configure each */}
          {step === 1 && (
            <div className="space-y-4">
              {selected.map(sid => {
                const s = allStaff.find(p => p.id === sid);
                const avatarUser = { id: s?.id, first_name: s?.first_name, last_name: s?.last_name, name: s?.full_name };
                const cfg = configs[sid] || {};
                return (
                  <div key={sid} className="soem-card p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <UserAvatar user={avatarUser} size="sm" />
                      <p className="font-semibold text-soem-navy">{s?.full_name}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="f-puesto-en-el-evento" className="soem-label block mb-1">Puesto en el evento</label>
                        <input id="f-puesto-en-el-evento" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                          value={cfg.position || ''} onChange={e => setConfigs(prev => ({...prev, [sid]: {...cfg, position: e.target.value}}))}
                          placeholder="Ej: Seguridad puerta principal" />
                      </div>
                      <div>
                        <label htmlFor="f-total-a-pagar" className="soem-label block mb-1">Total a pagar ($)</label>
                        <input id="f-total-a-pagar" type="number" min="0" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                          value={cfg.total_amount || ''} onChange={e => setConfigs(prev => ({...prev, [sid]: {...cfg, total_amount: e.target.value}}))}
                          placeholder="0.00" />
                      </div>
                    </div>
                    {/* Shift - multiple selection */}
                    <div>
                      <label htmlFor="f-turnos-puedes-seleccionar-varios" className="soem-label block mb-1.5">Turnos (puedes seleccionar varios)</label>
                      <div className="space-y-2">
                        {PRESET_SHIFTS.map(sh => {
                          const shifts = cfg.shifts || [];
                          const isSelected = shifts.some(s => s.name === sh.name);
                          return (
                            <label key={sh.name} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  const newShifts = isSelected 
                                    ? shifts.filter(s => s.name !== sh.name)
                                    : [...shifts, sh];
                                  setConfigs(prev => ({...prev, [sid]: {...cfg, shifts: newShifts, shift: newShifts[0] || null}}));
                                }}
                                className="w-4 h-4 rounded border-slate-300 text-soem-primary focus:ring-soem-primary"
                              />
                              <span className={`text-sm ${isSelected ? 'font-semibold text-soem-primary' : 'text-slate-600'}`}>
                                {sh.name} <span className="text-slate-400">{sh.start_time} – {sh.end_time}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                    {/* Zone */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label htmlFor="f-zona" className="soem-label block mb-1">Zona</label>
                        <input id="f-zona" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-soem-primary"
                          value={cfg.zone || ''} onChange={e => setConfigs(prev => ({...prev, [sid]: {...cfg, zone: e.target.value}}))}
                          placeholder="Ej: Acceso Norte" />
                      </div>
                      <div>
                        <label htmlFor="f-ubicación-específica" className="soem-label block mb-1">Ubicación específica</label>
                        <input id="f-ubicación-específica" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-soem-primary"
                          value={cfg.location_in_event || ''} onChange={e => setConfigs(prev => ({...prev, [sid]: {...cfg, location_in_event: e.target.value}}))}
                          placeholder="Ej: Entrada Principal" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Step 2: Equipment */}
          {step === 2 && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Selecciona el equipo que se entregará a <strong>todo el personal seleccionado</strong>.</p>
              {allEquip.map(eq => {
                const cat = EQUIPMENT_CATEGORIES[eq.category] || EQUIPMENT_CATEGORIES.otro;
                const eqState = sharedEquip[eq.id] || {};
                return (
                  <div key={eq.id} className={`border rounded-sm p-3 ${eqState.enabled ? 'border-soem-primary bg-blue-50' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setSharedEquip(prev => ({...prev, [eq.id]: {...eqState, enabled: !eqState.enabled}}))}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${eqState.enabled ? 'bg-soem-primary border-soem-primary' : 'border-slate-300'}`}>
                        {eqState.enabled && <Check size={12} className="text-white" />}
                      </button>
                      <span className="text-sm font-medium text-soem-navy">{cat.emoji} {eq.name}</span>
                    </div>
                    {eqState.enabled && (
                      <div className="grid grid-cols-2 gap-2 mt-2 ml-8">
                        <div>
                          <label htmlFor={`f-qty-${eq.id}`} className="soem-label block mb-0.5">Cantidad <span className="text-red-500">*</span></label>
                          <input id={`f-qty-${eq.id}`} type="number" min="1" className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none"
                            value={eqState.qty || ''} onChange={e => setSharedEquip(prev => ({...prev, [eq.id]: {...eqState, qty: e.target.value}}))}
                            placeholder="Ingrese cantidad" />
                        </div>
                        <div>
                          <label htmlFor={`f-cond-${eq.id}`} className="soem-label block mb-0.5">Condición</label>
                          <select id={`f-cond-${eq.id}`} className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none"
                            value={eqState.condition || 'bueno'} onChange={e => setSharedEquip(prev => ({...prev, [eq.id]: {...eqState, condition: e.target.value}}))}>
                            {['bueno','regular','malo'].map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          {step > 0 && <button onClick={() => setStep(s => s-1)} className="soem-btn-secondary px-4 py-2.5 text-sm">← Anterior</button>}
          {step < 2 ? (
            <button onClick={() => setStep(s => s+1)} disabled={step === 0 && selected.length === 0}
              className="soem-btn-primary flex-1 py-2.5 text-sm disabled:opacity-60">
              Siguiente →
            </button>
          ) : (
            <button onClick={handleAssign} disabled={saving || selected.length === 0}
              className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="confirm-assign-btn">
              {saving ? 'Asignando...' : `✓ Confirmar (${selected.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PaymentModal ────────────────────────────────────────────────────────────
function PaymentModal({ eventId, assignment, onPaid, onClose }) {
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [receipts, setReceipts] = useState([]); // urls subidas en esta sesión (#VNS042bj)
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const balance = assignment.balance || (assignment.total_amount - assignment.paid_amount);
  const history = assignment.payment_history || [];

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const r = await staffAPI.uploadReceipt(file);
      const url = r.data?.receipt_url;
      if (url) setReceipts((prev) => [...prev, { url, name: file.name }]);
      else toast.error('No se recibió URL del comprobante');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al subir comprobante');
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy flex-shrink-0">
          <h3 className="font-bold text-white">Registrar Pago</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="bg-slate-50 rounded-sm p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Total:</span><span className="font-bold">${assignment.total_amount?.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Pagado:</span><span className="text-green-600 font-bold">${assignment.paid_amount?.toLocaleString()}</span></div>
            <div className="flex justify-between border-t pt-1"><span className="text-slate-500">Pendiente:</span><span className="text-red-600 font-bold">${balance?.toLocaleString()}</span></div>
          </div>

          {/* Historial de pagos previos con sus notas y comprobantes (#2gH3RxAD). */}
          {history.length > 0 && (
            <div className="border border-slate-200 rounded-sm p-3 bg-white">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Historial de pagos</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {history.map((p, i) => (
                  <div key={p.id || i} className="text-xs border-l-2 border-soem-primary pl-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-soem-navy">${(p.amount_paid || 0).toLocaleString()}</span>
                      <span className="text-slate-400">{p.paid_at ? new Date(p.paid_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                    </div>
                    {p.payment_notes && (
                      <p className="text-slate-600 mt-0.5 italic">"{p.payment_notes}"</p>
                    )}
                    {p.receipt_url && (
                      <a href={p.receipt_url} target="_blank" rel="noreferrer" className="text-soem-primary hover:underline text-xs inline-flex items-center gap-1 mt-0.5">
                        📎 Ver comprobante
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="f-monto-a-pagar" className="soem-label block mb-1">Monto a pagar ($)</label>
            <div className="flex gap-2">
              <input id="f-monto-a-pagar" type="number" min="0" max={balance} className="flex-1 px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
              <button onClick={() => setAmount(String(balance))}
                className="px-4 py-2 bg-soem-primary text-white rounded-sm text-sm font-bold hover:bg-soem-primary-hover transition-colors"
                data-testid="btn-pago-total">
                Pago total
              </button>
            </div>
          </div>
          <div>
            <label htmlFor="f-notas" className="soem-label block mb-1">Notas</label>
            <input id="f-notas" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observaciones del pago..." />
          </div>
          {/* Adjuntar comprobantes (#VNS042bj). Permite múltiples archivos. */}
          <div>
            <label className="soem-label block mb-1">Comprobantes</label>
            <label className="block border-2 border-dashed border-slate-300 rounded-sm p-3 text-center text-xs text-slate-500 hover:border-soem-primary cursor-pointer">
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  for (const f of files) await uploadFile(f);
                  e.target.value = '';
                }}
              />
              {uploading ? 'Subiendo…' : 'Da clic para subir uno o varios comprobantes (PDF/imagen)'}
            </label>
            {receipts.length > 0 && (
              <div className="mt-2 space-y-1">
                {receipts.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs bg-blue-50 border border-blue-200 rounded-sm px-2 py-1">
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-soem-primary hover:underline truncate flex-1">📎 {r.name}</a>
                    <button onClick={() => setReceipts(prev => prev.filter((_, j) => j !== i))} className="text-red-500 hover:text-red-700 ml-2" title="Quitar">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button disabled={saving || !amount} onClick={async () => {
              setSaving(true);
              try {
                // Si hay varios comprobantes, registramos un pago por cada URL.
                if (receipts.length === 0) {
                  await staffAPI.recordPayment(eventId, assignment.id, parseFloat(amount), notes, null);
                } else {
                  // Primer pago con monto completo + primer comprobante,
                  // pagos adicionales con monto 0 sólo para guardar la URL...
                  // En realidad, el backend rechaza monto 0, así que solo
                  // adjuntamos el primer comprobante al pago.
                  // Para mantenerlo simple, concatenamos las URLs en una sola.
                  const url = receipts.map(r => r.url).join(' | ');
                  await staffAPI.recordPayment(eventId, assignment.id, parseFloat(amount), notes, url);
                }
                toast.success('Pago registrado');
                onPaid(); onClose();
              } catch(err) { toast.error(err?.response?.data?.detail || 'Error'); }
              finally { setSaving(false); }
            }} className="soem-btn-primary flex-1 py-2.5 text-sm disabled:opacity-60">
              {saving ? 'Guardando...' : 'Confirmar pago'}
            </button>
            <button onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AttendanceModal ──────────────────────────────────────────────────────────
function AttendanceModal({ eventId, assignments, onSaved, onClose }) {
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  // Filtro por turno en Pasar Lista (#VvcHWZZ1).
  const [shiftFilter, setShiftFilter] = useState('');
  const [records, setRecords] = useState(() =>
    Object.fromEntries(assignments.map(a => [a.staff_member_id, { status: 'presente', check_in: '', check_out: '', notes: '' }]))
  );
  const [saving, setSaving] = useState(false);
  const STATUSES = Object.entries(ATTENDANCE_STATUS);
  const avatarOf = (a) => ({ id: a.staff_member_id, name: a.staff?.full_name || '', first_name: '', last_name: '' });

  const allShifts = [...new Set(
    assignments.flatMap(a => (a.shifts || [a.shift]).filter(Boolean).map(s => s.name || `${s.start_time}-${s.end_time}`))
  )].sort();

  const filteredAssignments = assignments.filter(a => {
    if (!shiftFilter) return true;
    const shifts = (a.shifts || [a.shift]).filter(Boolean).map(s => s.name || `${s.start_time}-${s.end_time}`);
    return shifts.includes(shiftFilter);
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy flex-shrink-0">
          <div>
            <h3 className="font-bold text-white">Pasar Lista</h3>
            <input type="date" className="bg-transparent text-slate-300 text-xs mt-0.5 focus:outline-none"
              value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-300">{Object.values(records).filter(r => r.status).length}/{filteredAssignments.length} registrados</span>
            <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
          </div>
        </div>
        <div className="p-3 border-b border-slate-100 flex-shrink-0 flex flex-wrap items-center gap-2">
          {/* Toggle inteligente (#LTuKWl8C). Antes:
              (1) solo iteraba sobre Object.keys(prev) → no marcaba a
                  asignaciones sin record previo;
              (2) no había forma de desmarcar. Ahora si TODOS los
              filtrados ya están como 'presente', el click desmarca;
              si no, marca a todos los filtrados (incluyendo los sin
              record) como 'presente'. */}
          {(() => {
            const allMarkedPresent = filteredAssignments.length > 0
              && filteredAssignments.every(a => records[a.staff_member_id]?.status === 'presente');
            return (
              <button
                onClick={() => setRecords(prev => {
                  const next = { ...prev };
                  filteredAssignments.forEach(a => {
                    const cur = next[a.staff_member_id] || {};
                    next[a.staff_member_id] = {
                      ...cur,
                      status: allMarkedPresent ? null : 'presente',
                    };
                  });
                  return next;
                })}
                className="soem-btn-secondary px-3 py-1.5 text-xs"
              >
                {allMarkedPresent ? '↺ Desmarcar todos' : '✅ Marcar todos como presentes'}
              </button>
            );
          })()}
          {allShifts.length > 0 && (
            <select
              aria-label="Filtrar por turno"
              value={shiftFilter}
              onChange={e => setShiftFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-soem-primary"
              data-testid="attendance-shift-filter"
            >
              <option value="">Todos los turnos</option>
              {allShifts.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-slate-50">
          {filteredAssignments.map(a => {
            const r = records[a.staff_member_id] || {};
            return (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3">
                <UserAvatar user={avatarOf(a)} size="sm" />
                <p className="font-semibold text-soem-navy text-sm w-36 truncate">{a.staff?.full_name}</p>
                <div className="flex gap-1 flex-1">
                  {STATUSES.map(([k, v]) => (
                    <button key={k} type="button" onClick={() => setRecords(prev => ({...prev, [a.staff_member_id]: {...r, status: k}}))}
                      className={`flex-1 py-1.5 rounded-sm text-xs font-semibold border transition-all ${r.status === k ? `${v.bg} border-current` : 'border-slate-200 text-slate-400 hover:border-slate-300'}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex-shrink-0">
          <button disabled={saving} onClick={async () => {
            setSaving(true);
            try {
              const recs = assignments.map(a => ({
                staff_member_id: a.staff_member_id,
                attendance_status: records[a.staff_member_id]?.status || 'presente',
                check_in: records[a.staff_member_id]?.check_in || null,
                check_out: records[a.staff_member_id]?.check_out || null,
                notes: records[a.staff_member_id]?.notes || null,
              }));
              await staffAPI.recordAttendance(eventId, date, recs);
              toast.success('Lista guardada');
              onSaved(); onClose();
            } catch(err) { toast.error(err?.response?.data?.detail || 'Error'); }
            finally { setSaving(false); }
          }} className="soem-btn-primary w-full py-2.5 text-sm disabled:opacity-60">
            {saving ? 'Guardando...' : '✓ Guardar lista'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main EventStaffTab ───────────────────────────────────────────────────────
export default function EventStaffTab({ event }) {
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;
  const [assignments, setAssignments] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [qrPending, setQrPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('lista');
  const [showAssign, setShowAssign] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showMassEquip, setShowMassEquip] = useState(false);
  const [paymentModal, setPaymentModal] = useState(null);
  const [addEquipTo, setAddEquipTo] = useState(null); // asignación individual de equipo (#8HkrI7lg)
  const [attendanceShift, setAttendanceShift] = useState(''); // filtro de turno en pestaña Asistencia (#V4E1MLP6)
  const [expanded, setExpanded] = useState(null);
  const [trainingEdit, setTrainingEdit] = useState({});
  const [savingTraining, setSavingTraining] = useState(null);
  // #QrPendingProfile — modal con el perfil completo del registro QR
  // pendiente, así el admin puede revisar foto, datos de contacto,
  // dirección y contacto de emergencia antes de aprobar o rechazar.
  const [qrProfileModal, setQrProfileModal] = useState(null);
  
  // TAREA 18: Filters for staff list
  const [filters, setFilters] = useState({ 
    search: '', 
    shift: '', 
    position: '', 
    payment: '', 
    training: '' 
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, attRes, qrRes] = await Promise.all([
        staffAPI.eventStaff(event.id),
        staffAPI.getAttendance(event.id),
        staffAPI.getQrRegistrations(event.id).catch(() => ({ data: { items: [] } })),
      ]);
      setAssignments(staffRes.data.items || []);
      setAttendance(attRes.data.by_date || {});
      // staffAPI.getQrRegistrations ya normaliza la respuesta a un array
      // plano en r.data. Antes se pedía r.data.items que siempre era
      // undefined y dejaba qrPending vacío, escondiendo los registros
      // pendientes en la pestaña Personal (#UWkqAcQD).
      const qrList = Array.isArray(qrRes.data) ? qrRes.data : (qrRes.data?.items || []);
      setQrPending(qrList.filter(r => r.status === 'pending'));
    } catch { toast.error('Error al cargar personal'); }
    finally { setLoading(false); }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  // Save training for a specific assignment
  const saveTraining = async (assignId) => {
    setSavingTraining(assignId);
    try {
      const t = trainingEdit[assignId] || {};
      await staffAPI.updateAssignment(event.id, assignId, {
        training_completed: t.training_completed ?? false,
        training_date: t.training_completed && t.training_date ? t.training_date : null,
        training_notes: t.training_notes || null,
      });
      toast.success('Capacitación guardada');
      load();
    } catch { toast.error('Error al guardar capacitación'); }
    finally { setSavingTraining(null); }
  };

  // Initialize training edit state from assignment
  const getTrainingEdit = (a) => {
    if (trainingEdit[a.id]) return trainingEdit[a.id];
    return {
      training_completed: a.training_completed || false,
      training_date: a.training_date || '',
      training_notes: a.training_notes || '',
    };
  };

  // Approve QR registration
  const approveQR = async (reg) => {
    try {
      await apiClient.patch(`/qr-registrations/${reg.id}/approve`);
      setQrPending(prev => prev.filter(r => r.id !== reg.id));
      toast.success(`${reg.full_name} aprobado`);
    } catch { toast.error('Error al aprobar'); }
  };

  const rejectQR = async (reg) => {
    try {
      await apiClient.patch(`/qr-registrations/${reg.id}/reject`);
      setQrPending(prev => prev.filter(r => r.id !== reg.id));
      // Toast en rojo — rechazo no es éxito (#2cmmE4eR).
      toast.error('Registro rechazado');
    } catch { toast.error('Error'); }
  };

  // Devolución de equipo individual con condición (#PhadD9Ih, #UBTKYoRV).
  const returnEquipmentItem = async (assignId, eq, condition) => {
    const pending = (eq.quantity_assigned || 0) - (eq.quantity_returned || 0);
    if (pending <= 0) return;
    const verb = condition === 'dañado' ? 'marcar como dañado' : 'devolver';
    if (!window.confirm(`¿${verb} ${pending} unidad(es) de "${eq.item_name}"?`)) return;
    try {
      await staffAPI.returnEquipment(eventId, assignId, eq.id, {
        quantity_returned: pending,
        condition_on_return: condition,
      });
      toast.success(condition === 'dañado' ? 'Equipo registrado como dañado' : 'Equipo devuelto');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al actualizar equipo');
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setFont('helvetica','bold');
    doc.text(`Control de Personal — ${event.name}`, 14, 20);
    doc.setFontSize(9); doc.setFont('helvetica','normal');
    doc.text(`Generado: ${new Date().toLocaleString('es-MX')}`, 14, 28);
    let y = 38;
    doc.setFontSize(11); doc.setFont('helvetica','bold');
    doc.text('Nómina del Evento', 14, y); y += 8;
    const totalPay = assignments.reduce((s,a) => s + (a.total_amount||0), 0);
    const totalPaid = assignments.reduce((s,a) => s + (a.paid_amount||0), 0);
    const totalBalance = assignments.reduce((s,a) => s + (a.balance||0), 0);
    doc.setFont('helvetica','normal'); doc.setFontSize(9);
    doc.text(`Total nómina: $${totalPay.toLocaleString()} | Pagado: $${totalPaid.toLocaleString()} | Pendiente: $${totalBalance.toLocaleString()}`, 14, y); y += 8;
    assignments.forEach(a => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.text(`${a.staff?.full_name || '—'} | ${a.position_in_event || '—'} | ${a.payment_status} | $${(a.paid_amount||0)} / $${(a.total_amount||0)}`, 14, y); y += 6;
    });
    doc.save(`personal-${event.name.replace(/\s+/g,'-').toLowerCase()}.pdf`);
    toast.success('PDF descargado');
  };

  const totals = {
    total: assignments.length,
    pendingPay: assignments.filter(a => a.payment_status !== 'pagado').reduce((s,a) => s + (a.balance||0), 0),
    paid: assignments.filter(a => a.payment_status === 'pagado').length,
    pendingEquip: assignments.reduce((s, a) => s + (a.equipment||[]).reduce((eq, e) => eq + (e.quantity_assigned - e.quantity_returned), 0), 0),
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-6 w-6 border-4 border-soem-primary border-t-transparent" /></div>;

  return (
    <div className="space-y-5" data-testid="event-staff-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total asignados', value: totals.total, color: '#1A56DB', icon: Users },
          { label: 'Pagos pendientes', value: `$${totals.pendingPay.toLocaleString()}`, color: '#DC2626', icon: DollarSign },
          { label: 'Personal pagado', value: totals.paid, color: '#15803D', icon: Check },
          { label: 'Equipo sin devolver', value: totals.pendingEquip, color: '#D97706', icon: Package },
        ].map(s => (
          <div key={s.label} className="soem-card p-4 border-t-4" style={{ borderTopColor: s.color }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="soem-label">{s.label}</p>
                <p className="text-xl font-black text-soem-navy">{s.value}</p>
              </div>
              <s.icon size={20} style={{ color: s.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <>
            <button onClick={() => setShowAssign(true)} className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2" data-testid="assign-staff-btn">
              <Plus size={15} /> Asignar Personal
            </button>
            <button onClick={() => setShowAttendance(true)} className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="attendance-btn">
              <ClipboardList size={15} /> Pasar Lista
            </button>
            <button onClick={() => setShowMassEquip(true)} className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2">
              <Package size={15} /> Equipar Personal
            </button>
            <button onClick={() => setShowQR(true)} className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="qr-btn">
              🔲 Generar QR
            </button>
          </>
        )}
        <button onClick={exportPDF} className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2">
          <Download size={15} /> Descargar Reporte PDF
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-slate-200 flex-wrap">
        {[
          { id: 'lista', label: `Lista (${assignments.length})`, badge: qrPending.length > 0 ? qrPending.length : 0 },
          { id: 'asistencia', label: 'Asistencia' },
          { id: 'distribucion', label: 'Distribución' },
          { id: 'inventario', label: 'Inventario de Equipo' },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`relative px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${subTab === t.id ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`subtab-${t.id}`}>
            {t.label}
            {t.badge > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* QR Pending banner */}
      {subTab === 'lista' && qrPending.length > 0 && (
        <div className="soem-card overflow-hidden border-l-4 border-l-orange-500" data-testid="qr-pending-section">
          <div className="flex items-center justify-between px-5 py-3 bg-orange-50 border-b border-orange-100">
            <div className="flex items-center gap-2">
              <span className="text-lg">⏳</span>
              <p className="font-bold text-orange-800 text-sm">Pendientes de aprobación vía QR ({qrPending.length})</p>
            </div>
          </div>
          <div className="divide-y divide-slate-50">
            {qrPending.map(reg => (
              <div key={reg.id} className="flex items-center gap-3 px-5 py-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-bold text-xs flex-shrink-0">
                  QR
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-soem-navy text-sm">{reg.full_name}</p>
                  <p className="text-slate-400 text-xs">
                    {reg.phone} · {new Date(reg.registered_at).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    {' · '}<span className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0.5 rounded font-medium">Via QR</span>
                  </p>
                </div>
                {canEdit && (
                  <div className="flex gap-2 flex-shrink-0">
                    {/* #QrPendingProfile — botón Ver perfil antes de
                        Aprobar/Rechazar. Marco quería revisar foto,
                        teléfono, dirección y contacto de emergencia
                        antes de tomar la decisión. */}
                    <button onClick={() => setQrProfileModal(reg)}
                      className="bg-white border border-slate-300 text-slate-700 text-xs px-3 py-1.5 rounded-sm font-semibold hover:bg-slate-50 transition-colors"
                      data-testid={`view-qr-${reg.id}`}>
                      👁 Ver perfil
                    </button>
                    <button onClick={() => approveQR(reg)}
                      className="bg-green-600 text-white text-xs px-3 py-1.5 rounded-sm font-semibold hover:bg-green-700 transition-colors"
                      data-testid={`approve-qr-${reg.id}`}>
                      ✅ Aprobar
                    </button>
                    <button onClick={() => rejectQR(reg)}
                      className="bg-red-100 text-red-700 text-xs px-3 py-1.5 rounded-sm font-semibold hover:bg-red-200 transition-colors"
                      data-testid={`reject-qr-${reg.id}`}>
                      ❌ Rechazar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sub-tab 1: Lista */}
      {subTab === 'lista' && (
        <div className="soem-card overflow-hidden">
          {/* TAREA 18: Filters bar */}
          {assignments.length > 0 && (
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[200px]">
                  <input 
                    type="text" 
                    placeholder="🔍 Buscar por nombre..." 
                    value={filters.search} aria-label="Buscar personal"
                    onChange={e => setFilters(f => ({...f, search: e.target.value}))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                    data-testid="filter-search"
                  />
                </div>
                <select value={filters.shift} aria-label="Filtrar por turno" onChange={e => setFilters(f => ({...f, shift: e.target.value}))}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                  data-testid="filter-shift">
                  <option value="">Todos los turnos</option>
                  {[...new Set(assignments.flatMap(a => (a.shifts || [a.shift]).filter(Boolean).map(s => s.name || `${s.start_time}-${s.end_time}`)))].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <select value={filters.position} aria-label="Filtrar por puesto" onChange={e => setFilters(f => ({...f, position: e.target.value}))}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                  data-testid="filter-position">
                  <option value="">Todos los puestos</option>
                  {[...new Set(assignments.map(a => a.role || a.position).filter(Boolean))].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                <select value={filters.payment} aria-label="Filtrar por pago" onChange={e => setFilters(f => ({...f, payment: e.target.value}))}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                  data-testid="filter-payment">
                  <option value="">Todos los pagos</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="parcial">Parcial</option>
                  <option value="pagado">Pagado</option>
                </select>
                <select value={filters.training} aria-label="Filtrar por capacitación" onChange={e => setFilters(f => ({...f, training: e.target.value}))}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                  data-testid="filter-training">
                  <option value="">Toda la capacitación</option>
                  <option value="completada">Completada</option>
                  <option value="pendiente">Pendiente</option>
                </select>
                {(filters.search || filters.shift || filters.position || filters.payment || filters.training) && (
                  <button onClick={() => setFilters({ search: '', shift: '', position: '', payment: '', training: '' })}
                    className="px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                    data-testid="filter-clear">
                    ✕ Limpiar filtros
                  </button>
                )}
              </div>
            </div>
          )}
          {(() => {
            // Apply filters
            const filteredAssignments = assignments.filter(a => {
              if (filters.search) {
                const name = (a.staff?.full_name || a.staff?.first_name + ' ' + a.staff?.last_name || '').toLowerCase();
                if (!name.includes(filters.search.toLowerCase())) return false;
              }
              if (filters.shift) {
                const shifts = (a.shifts || [a.shift]).filter(Boolean).map(s => s.name || `${s.start_time}-${s.end_time}`);
                if (!shifts.includes(filters.shift)) return false;
              }
              if (filters.position && (a.role || a.position) !== filters.position) return false;
              if (filters.payment && a.payment_status !== filters.payment) return false;
              if (filters.training) {
                if (filters.training === 'completada' && !a.training_completed) return false;
                if (filters.training === 'pendiente' && a.training_completed) return false;
              }
              return true;
            });
            
            return filteredAssignments.length === 0 ? (
            <div className="p-10 text-center">
              <Users size={36} className="text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400">No hay personal asignado a este evento.</p>
              {canEdit && <button onClick={() => setShowAssign(true)} className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2"><Plus size={14}/>Asignar</button>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="soem-table-header">
                  <th className="px-4 py-3 text-left">Personal</th>
                  <th className="px-4 py-3 text-left">Puesto</th>
                  <th className="px-4 py-3 text-left">Turno / Zona</th>
                  <th className="px-4 py-3 text-left">Capacitación</th>
                  <th className="px-4 py-3 text-left">Pago</th>
                  <th className="px-4 py-3 text-left">Equipo</th>
                  {canEdit && <th className="px-4 py-3 text-left">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.map(a => {
                  const payCfg = PAYMENT_STATUS[a.payment_status] || PAYMENT_STATUS.pendiente;
                  const avatarUser = { id: a.staff_member_id, name: a.staff?.full_name || '', first_name: '', last_name: '' };
                  const pendingEquip = (a.equipment||[]).reduce((s,e) => s + (e.quantity_assigned - e.quantity_returned), 0);
                  const isExp = expanded === a.id;
                  return (
                    <React.Fragment key={a.id}>
                      <tr className="soem-table-row cursor-pointer" onClick={() => setExpanded(isExp ? null : a.id)}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <UserAvatar user={avatarUser} size="sm" />
                            <p className="font-semibold text-soem-navy">{a.staff?.full_name || '—'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 text-xs">{a.position_in_event || '—'}</td>
                        <td className="px-4 py-2.5 text-xs">
                          <div className="space-y-0.5">
                            {/* Show all shifts (TAREA 2) */}
                            {(a.shifts && a.shifts.length > 0) ? (
                              <div className="flex flex-wrap gap-1">
                                {a.shifts.map((shift, idx) => (
                                  <span key={idx} className="inline-flex items-center bg-blue-50 text-blue-700 border border-blue-200 text-xs px-2 py-0.5 rounded-full font-medium">
                                    🕐 {shift.name || `${shift.start_time}–${shift.end_time}`}
                                  </span>
                                ))}
                              </div>
                            ) : a.shift ? (
                              <span className="inline-flex items-center bg-blue-50 text-blue-700 border border-blue-200 text-xs px-2 py-0.5 rounded-full font-medium">
                                🕐 {a.shift.name || `${a.shift.start_time}–${a.shift.end_time}`}
                              </span>
                            ) : <span className="text-slate-300 text-xs">Sin turno</span>}
                            {a.zone && (
                              <span className="inline-flex items-center bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
                                📍 {a.zone}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-xs">
                          {a.training_completed
                            ? <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-semibold">✅ Completada</span>
                            : <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded-full font-semibold">⏳ Pendiente</span>
                          }
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="space-y-0.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${payCfg.bg}`}>{payCfg.label}</span>
                            <p className="text-xs text-slate-400">${(a.paid_amount||0).toLocaleString()} / ${(a.total_amount||0).toLocaleString()}</p>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          {(a.equipment||[]).length > 0 ? (() => {
                            // TAREA 16: Only count items with quantity_assigned > 0
                            const activeItems = (a.equipment||[]).filter(e => Number(e.quantity_assigned || e.quantity || 0) > 0);
                            const totalPending = activeItems.reduce((s, e) => s + (Number(e.quantity_assigned || e.quantity || 0) - Number(e.quantity_returned || 0)), 0);
                            return activeItems.length > 0 ? (
                              <span className={`text-xs font-semibold ${totalPending > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                {activeItems.length} artículo(s){totalPending > 0 ? ` · ${totalPending} pend.` : ' ✓'}
                              </span>
                            ) : <span className="text-slate-300 text-xs">Sin equipo</span>;
                          })() : <span className="text-slate-300 text-xs">Sin equipo</span>}
                        </td>
                        {canEdit && (
                          <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setPaymentModal(a)}
                              className="text-xs text-soem-primary hover:underline font-semibold">
                              Registrar pago
                            </button>
                          </td>
                        )}
                      </tr>
                      {isExp && (
                        <tr>
                          <td colSpan={canEdit ? 6 : 5} className="bg-blue-50 px-6 py-4 border-b border-slate-100">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Payment summary */}
                              <div>
                                <p className="soem-label mb-2">Resumen de pago</p>
                                <div className="bg-white rounded-sm p-3 border border-slate-200 text-xs space-y-1">
                                  <div className="flex justify-between"><span className="text-slate-500">Total:</span><span className="font-bold">${(a.total_amount||0).toLocaleString()}</span></div>
                                  <div className="flex justify-between"><span className="text-slate-500">Pagado:</span><span className="text-green-600 font-bold">${(a.paid_amount||0).toLocaleString()}</span></div>
                                  <div className="flex justify-between border-t pt-1"><span className="text-slate-500">Pendiente:</span><span className={`font-bold ${(a.balance||0) > 0 ? 'text-red-600' : 'text-green-600'}`}>${(a.balance||0).toLocaleString()}</span></div>
                                </div>
                              </div>
                              {/* Equipment */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <p className="soem-label mb-0">Equipo asignado</p>
                                  {canEdit && (
                                    // Asignación individual (#8HkrI7lg).
                                    <button
                                      onClick={() => setAddEquipTo(a)}
                                      className="text-xs text-soem-primary hover:underline flex items-center gap-1"
                                      data-testid={`add-equipment-${a.id}`}
                                    >
                                      <Plus size={12} /> Agregar
                                    </button>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  {(a.equipment||[]).length === 0 ? <p className="text-slate-400 text-xs">Sin equipo</p> : (a.equipment||[]).map(eq => {
                                    const pending = (eq.quantity_assigned || 0) - (eq.quantity_returned || 0);
                                    return (
                                      <div key={eq.id} className={`flex items-center justify-between gap-2 text-xs p-2 rounded-sm ${pending > 0 ? 'bg-orange-50 border border-orange-200' : 'bg-white border border-slate-200'}`}>
                                        <span className="font-medium flex-1 truncate">{eq.item_name}</span>
                                        <span className={pending > 0 ? 'text-orange-600 font-bold' : 'text-green-600'}>
                                          {eq.quantity_returned || 0}/{eq.quantity_assigned} devuelto(s)
                                        </span>
                                        {canEdit && pending > 0 && (
                                          // Devolver (#PhadD9Ih) + marcar dañado (#UBTKYoRV).
                                          <div className="flex gap-1 flex-shrink-0">
                                            <button
                                              onClick={() => returnEquipmentItem(a.id, eq, 'bueno')}
                                              className="px-2 py-0.5 bg-green-600 text-white rounded text-[10px] font-semibold hover:bg-green-700"
                                              title="Devolver en buen estado"
                                            >✓ Devolver</button>
                                            <button
                                              onClick={() => returnEquipmentItem(a.id, eq, 'dañado')}
                                              className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] font-semibold hover:bg-red-700"
                                              title="Marcar como dañado"
                                            >⚠ Dañado</button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              {/* ─── CAPACITACIÓN ─────────────────────── */}
                              {canEdit && (() => {
                                const te = getTrainingEdit(a);
                                return (
                                  <div>
                                    <p className="soem-label mb-2">Capacitación</p>
                                    <div className="bg-white rounded-sm p-3 border border-slate-200 space-y-3">
                                      {/* TAREA 19: Two buttons always visible */}
                                      <div>
                                        <span className="text-xs text-slate-600 block mb-2">¿Tomó la capacitación del evento?</span>
                                        <div className="flex gap-2">
                                          <button type="button"
                                            onClick={() => setTrainingEdit(prev => ({
                                              ...prev,
                                              [a.id]: { ...te, training_completed: true }
                                            }))}
                                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold border-2 transition-all ${
                                              te.training_completed
                                                ? 'bg-green-600 border-green-600 text-white'
                                                : 'bg-white border-slate-300 text-slate-500 hover:border-green-400'
                                            }`}
                                            data-testid={`training-yes-${a.id}`}>
                                            ✅ SÍ — Completada
                                          </button>
                                          <button type="button"
                                            onClick={() => setTrainingEdit(prev => ({
                                              ...prev,
                                              [a.id]: { ...te, training_completed: false }
                                            }))}
                                            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold border-2 transition-all ${
                                              !te.training_completed
                                                ? 'bg-red-600 border-red-600 text-white'
                                                : 'bg-white border-slate-300 text-slate-500 hover:border-red-400'
                                            }`}
                                            data-testid={`training-no-${a.id}`}>
                                            ❌ NO — Pendiente
                                          </button>
                                        </div>
                                      </div>
                                      {te.training_completed && (
                                        <div>
                                          <label htmlFor="f-fecha-de-capacitación" className="soem-label block mb-1">Fecha de capacitación</label>
                                          <input id="f-fecha-de-capacitación" type="date"
                                            className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs focus:outline-none focus:ring-1 focus:ring-soem-primary"
                                            value={te.training_date || ''}
                                            onChange={e => setTrainingEdit(prev => ({
                                              ...prev,
                                              [a.id]: { ...te, training_date: e.target.value }
                                            }))}
                                          />
                                        </div>
                                      )}
                                      <div>
                                        <label htmlFor="f-notas-1" className="soem-label block mb-1">Notas</label>
                                        <textarea
id="f-notas-1"                                           className="w-full px-2 py-1.5 border border-slate-300 rounded-sm text-xs resize-none focus:outline-none focus:ring-1 focus:ring-soem-primary"
                                          rows={2}
                                          placeholder="Observaciones de la capacitación..."
                                          value={te.training_notes || ''}
                                          onChange={e => setTrainingEdit(prev => ({
                                            ...prev,
                                            [a.id]: { ...te, training_notes: e.target.value }
                                          }))}
                                        />
                                      </div>
                                      <button
                                        onClick={() => saveTraining(a.id)}
                                        disabled={savingTraining === a.id}
                                        className="soem-btn-primary w-full py-1.5 text-xs flex items-center justify-center gap-1.5 disabled:opacity-60"
                                        data-testid={`save-training-${a.id}`}>
                                        {savingTraining === a.id
                                          ? <><span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> Guardando...</>
                                          : '💾 Guardar capacitación'
                                        }
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          );
          })()}
        </div>
      )}

      {/* Sub-tab 2: Attendance */}
      {subTab === 'asistencia' && (
        <div className="space-y-4">
          {/* Filtro por turno también en el resumen (#V4E1MLP6). */}
          {Object.keys(attendance).length > 0 && (() => {
            const allShifts = [...new Set(assignments.flatMap(a => (a.shifts || [a.shift]).filter(Boolean).map(s => s.name || `${s.start_time}-${s.end_time}`)))].sort();
            if (allShifts.length === 0) return null;
            return (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Ver por turno:</span>
                <select
                  aria-label="Filtrar asistencia por turno"
                  value={attendanceShift}
                  onChange={(e) => setAttendanceShift(e.target.value)}
                  className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs focus:outline-none focus:ring-2 focus:ring-soem-primary"
                  data-testid="attendance-resume-shift"
                >
                  <option value="">Todos los turnos</option>
                  {allShifts.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            );
          })()}
          {Object.keys(attendance).length === 0 ? (
            <div className="soem-card p-10 text-center">
              <ClipboardList size={36} className="text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400">No hay listas de asistencia registradas.</p>
              {canEdit && <button onClick={() => setShowAttendance(true)} className="soem-btn-primary px-5 py-2 text-sm mt-4 inline-flex items-center gap-2"><Plus size={14}/>Pasar lista</button>}
            </div>
          ) : (
            Object.entries(attendance).sort(([a],[b]) => b.localeCompare(a)).map(([date, records]) => {
              // Filtrar registros por turno usando los shifts de la asignación.
              const filteredRecords = !attendanceShift ? records : records.filter(r => {
                const a = assignments.find(x => x.staff_member_id === r.staff_member_id);
                if (!a) return false;
                const shifts = (a.shifts || [a.shift]).filter(Boolean).map(s => s.name || `${s.start_time}-${s.end_time}`);
                return shifts.includes(attendanceShift);
              });
              if (filteredRecords.length === 0) return null;
              const present = filteredRecords.filter(r => r.attendance_status === 'presente').length;
              const absent = filteredRecords.filter(r => r.attendance_status === 'ausente').length;
              const late = filteredRecords.filter(r => r.attendance_status === 'tardanza').length;
              return (
                <div key={date} className="soem-card overflow-hidden">
                  <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <p className="font-semibold text-soem-navy">{new Date(date + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                    <div className="flex gap-3 text-xs">
                      <span className="text-green-600 font-semibold">✅ {present}</span>
                      <span className="text-yellow-600 font-semibold">⏰ {late}</span>
                      <span className="text-red-600 font-semibold">❌ {absent}</span>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-50">
                    {filteredRecords.map(r => {
                      const stCfg = ATTENDANCE_STATUS[r.attendance_status] || ATTENDANCE_STATUS.presente;
                      return (
                        <div key={r.id || r.staff_member_id} className="flex items-center gap-3 px-5 py-2.5">
                          <p className="text-sm font-medium text-soem-navy flex-1">{r.staff_name || r.staff_member_id}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${stCfg.bg}`}>{stCfg.label}</span>
                          <p className="text-xs text-slate-400">{r.check_in || '—'} → {r.check_out || '—'}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Sub-tab 3: Inventory */}
      {subTab === 'inventario' && (
        <div className="space-y-4">
          {(() => {
            // Aggregate by item name
            const byItem = {};
            assignments.forEach(a => {
              (a.equipment||[]).forEach(eq => {
                if (!byItem[eq.item_name]) byItem[eq.item_name] = { assigned: 0, returned: 0, pending: 0, lost: 0 };
                byItem[eq.item_name].assigned += eq.quantity_assigned;
                byItem[eq.item_name].returned += eq.quantity_returned;
                byItem[eq.item_name].pending += (eq.quantity_assigned - eq.quantity_returned);
                if (['perdido','dañado'].includes(eq.condition_on_return)) byItem[eq.item_name].lost++;
              });
            });
            const lostItems = Object.values(byItem).some(v => v.lost > 0);
            return (
              <>
                {lostItems && (
                  <div className="bg-red-50 border border-red-200 rounded-sm p-3 flex items-center gap-2 text-red-800 text-sm">
                    <Package size={16} /> Hay artículos perdidos o dañados. Revisa el detalle.
                  </div>
                )}
                <div className="soem-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="soem-table-header">
                        <th className="px-5 py-3 text-left">Artículo</th>
                        <th className="px-5 py-3 text-center">Asignado</th>
                        <th className="px-5 py-3 text-center">Devuelto</th>
                        <th className="px-5 py-3 text-center">Pendiente</th>
                        <th className="px-5 py-3 text-center">Perdidos/Dañados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(byItem).length === 0 ? (
                        <tr><td colSpan={5} className="text-center text-slate-400 py-8">Sin equipo asignado</td></tr>
                      ) : (
                        Object.entries(byItem).map(([name, stats]) => (
                          <tr key={name} className={`soem-table-row ${stats.lost > 0 ? 'bg-red-50' : stats.pending > 0 ? 'bg-orange-50' : ''}`}>
                            <td className="px-5 py-3 font-medium text-soem-navy">{name}</td>
                            <td className="px-5 py-3 text-center font-mono">{stats.assigned}</td>
                            <td className="px-5 py-3 text-center font-mono text-green-600">{stats.returned}</td>
                            <td className="px-5 py-3 text-center">
                              {stats.pending > 0 ? <span className="text-orange-600 font-bold">{stats.pending} 🟠</span> : <span className="text-green-600">0 ✅</span>}
                            </td>
                            <td className="px-5 py-3 text-center">
                              {stats.lost > 0 ? <span className="text-red-600 font-bold">{stats.lost} 🔴</span> : <span className="text-slate-400">0</span>}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Sub-tab: Distribución */}
      {subTab === 'distribucion' && (
        <DistributionTab eventId={event.id} assignments={assignments} onRefresh={load} />
      )}

      {/* Modals */}
      {showAssign && (
        <AssignStaffModal eventId={event.id} existingIds={assignments.map(a => a.staff_member_id)}
          onAssigned={load} onClose={() => setShowAssign(false)} />
      )}
      {showAttendance && (
        <AttendanceModal eventId={event.id} assignments={assignments}
          onSaved={load} onClose={() => setShowAttendance(false)} />
      )}
      {paymentModal && (
        <PaymentModal eventId={event.id} assignment={paymentModal}
          onPaid={load} onClose={() => setPaymentModal(null)} />
      )}
      {showQR && (
        <QRModal event={event} onClose={() => setShowQR(false)} />
      )}
      {showMassEquip && (
        <MassEquipmentModal eventId={event.id} assignments={assignments}
          onDone={load} onClose={() => setShowMassEquip(false)} />
      )}
      {addEquipTo && (
        <AddEquipmentModal
          eventId={event.id}
          assignment={addEquipTo}
          onDone={load}
          onClose={() => setAddEquipTo(null)}
        />
      )}
      {/* #QrPendingProfile — modal con perfil completo del registro QR. */}
      {qrProfileModal && (
        <QrProfileModal
          reg={qrProfileModal}
          canEdit={canEdit}
          onApprove={async () => { await approveQR(qrProfileModal); setQrProfileModal(null); }}
          onReject={async () => { await rejectQR(qrProfileModal); setQrProfileModal(null); }}
          onClose={() => setQrProfileModal(null)}
        />
      )}
    </div>
  );
}

// ─── QrProfileModal ──────────────────────────────────────────────────────────
// #QrPendingProfile — Modal con el perfil completo de un registro QR
// pendiente: foto, datos personales, contacto, dirección y contacto de
// emergencia. Permite aprobar/rechazar desde aquí también.
function QrProfileModal({ reg, canEdit, onApprove, onReject, onClose }) {
  if (!reg) return null;
  const addr = reg.address || {};
  const addrStr = [addr.street, addr.ext_number, addr.neighborhood, addr.postal_code, addr.city, addr.state]
    .filter(Boolean).join(', ');
  const ec = reg.emergency_contact || {};
  const med = reg.medical_info || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="qr-profile-modal">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy sticky top-0">
          <h3 className="font-bold text-white">Perfil de registro QR</h3>
          <button aria-label="Cerrar" onClick={onClose}>
            <span className="text-slate-400 hover:text-white text-lg">×</span>
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Foto + nombre */}
          <div className="flex items-center gap-4">
            {reg.photo || reg.photo_url ? (
              <img src={reg.photo || reg.photo_url} alt={reg.full_name}
                className="w-20 h-20 rounded-full object-cover border-2 border-slate-200" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-3xl text-slate-400">
                {(reg.first_name || reg.full_name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-soem-navy text-lg">{reg.full_name || `${reg.first_name || ''} ${reg.last_name || ''}`.trim()}</p>
              <p className="text-slate-500 text-sm">{reg.position || <span className="italic">Sin puesto</span>}</p>
              <p className="text-slate-400 text-xs mt-1">Registrado: {reg.registered_at ? new Date(reg.registered_at).toLocaleString('es-MX') : '—'}</p>
            </div>
          </div>

          {/* Datos personales */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 uppercase">Género</p>
              <p className="text-slate-700 capitalize">{reg.gender || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase">Edad</p>
              <p className="text-slate-700">{reg.age || '—'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-slate-400 uppercase">Teléfono</p>
              <p className="text-slate-700">{reg.phone || '—'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-slate-400 uppercase">Email</p>
              <p className="text-slate-700 truncate">{reg.email || '—'}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-slate-400 uppercase">Dirección</p>
              <p className="text-slate-700">{addrStr || '—'}</p>
            </div>
          </div>

          {/* Contacto de emergencia */}
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-bold text-slate-500 uppercase mb-2">Contacto de emergencia</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2">
                <p className="text-xs text-slate-400">Nombre</p>
                <p className="text-slate-700">{ec.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Parentesco</p>
                <p className="text-slate-700">{ec.relationship || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Teléfono</p>
                <p className="text-slate-700">{ec.phone || '—'}</p>
              </div>
            </div>
          </div>

          {/* Info médica si aplica */}
          {(med.has_condition || med.uses_substances || med.has_disability || reg.imss_number) && (
            <div className="border-t border-slate-100 pt-3">
              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Información médica</p>
              <div className="space-y-1.5 text-sm">
                {reg.imss_number && <p className="text-slate-700">NSS IMSS: <span className="font-mono">{reg.imss_number}</span></p>}
                {med.has_condition && (
                  <p className="text-slate-700">⚠️ Condición médica: <span className="italic">{med.condition_detail || 'No especificada'}</span></p>
                )}
                {med.uses_substances && <p className="text-orange-700">⚠️ Reportó consumo de sustancias</p>}
                {med.has_disability && (
                  <p className="text-slate-700">♿ Discapacidad: <span className="italic">{med.disability_detail || 'No especificada'}</span></p>
                )}
              </div>
            </div>
          )}

          {/* Acciones */}
          {canEdit && (
            <div className="flex gap-3 pt-3 border-t border-slate-100">
              <button onClick={onApprove}
                className="bg-green-600 text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-green-700 flex-1 transition-colors"
                data-testid="qr-profile-approve">
                ✅ Aprobar
              </button>
              <button onClick={onReject}
                className="bg-red-100 text-red-700 px-4 py-2 rounded-sm text-sm font-semibold hover:bg-red-200 flex-1 transition-colors"
                data-testid="qr-profile-reject">
                ❌ Rechazar
              </button>
              <button onClick={onClose}
                className="bg-slate-100 text-slate-600 px-4 py-2 rounded-sm text-sm font-semibold hover:bg-slate-200 transition-colors">
                Cancelar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AddEquipmentModal ────────────────────────────────────────────────────────
// Asignación individual de equipo (#8HkrI7lg). Permite escoger un artículo
// del catálogo y cantidad para una sola persona.
function AddEquipmentModal({ eventId, assignment, onDone, onClose }) {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipmentId, setEquipmentId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [condition, setCondition] = useState('bueno');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await staffAPI.listEquipmentCatalog();
        const items = (r.data?.items || r.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
        setCatalog(items);
      } catch { toast.error('Error al cargar catálogo'); }
      finally { setLoading(false); }
    })();
  }, []);

  const submit = async () => {
    if (!equipmentId) { toast.error('Selecciona un artículo'); return; }
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) { toast.error('Cantidad inválida'); return; }
    setSaving(true);
    try {
      await staffAPI.addEquipment(eventId, assignment.id, equipmentId, qty, condition, notes || null);
      toast.success('Equipo agregado');
      onDone(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al agregar equipo');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-sm animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white">Agregar equipo</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-500">
            Asignar a <strong className="text-soem-navy">{assignment.staff?.full_name || 'esta persona'}</strong>
          </p>
          <div>
            <label className="soem-label block mb-1">Artículo</label>
            <select className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} disabled={loading}>
              <option value="">— Seleccionar artículo —</option>
              {catalog.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="soem-label block mb-1">Cantidad</label>
              <input type="number" min="1" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <div>
              <label className="soem-label block mb-1">Condición</label>
              <select className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
                value={condition} onChange={(e) => setCondition(e.target.value)}>
                <option value="bueno">Bueno</option>
                <option value="regular">Regular</option>
                <option value="malo">Malo</option>
              </select>
            </div>
          </div>
          <div>
            <label className="soem-label block mb-1">Notas (opcional)</label>
            <input className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
              value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones..." />
          </div>
          <div className="flex gap-3 pt-2">
            <button disabled={saving} onClick={submit} className="soem-btn-primary flex-1 py-2.5 text-sm disabled:opacity-60">
              {saving ? 'Guardando...' : 'Agregar'}
            </button>
            <button onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
